import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { apiGet, apiPatch } from '../services/api';
import { fmtDate as _fmtDateUTC } from '../services/dates';
import './EstadisticasPage.css';

const STATUS_LABEL = {
  reservado:   { label: 'En proceso · 100€', bg: '#fef3c7', color: '#92400e' },
  primer_pago: { label: '70% pagado',        bg: '#dbeafe', color: '#1e40af' },
  firmado:     { label: 'Cerrado',            bg: '#dcfce7', color: '#166534' },
  ganado:      { label: 'Ganado',             bg: '#dcfce7', color: '#166534' },
};

function StatusBadge({ status }) {
  const s = STATUS_LABEL[status] || { label: status, bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{background:s.bg,color:s.color,padding:'.18rem .55rem',borderRadius:10,fontSize:'.7rem',fontWeight:700,whiteSpace:'nowrap'}}>{s.label}</span>
  );
}

function EditableCell({ value, onSave, disabled, placeholder }) {
  const [v, setV]       = useState(value ?? '');
  const [dirty, setD]   = useState(false);
  useEffect(() => { setV(value ?? ''); setD(false); }, [value]);
  return (
    <input
      type="number" min="0" step="0.01" disabled={disabled}
      value={v}
      placeholder={placeholder || '0,00'}
      onChange={e => { setV(e.target.value); setD(true); }}
      onBlur={() => { if (dirty) { onSave(v === '' ? 0 : parseFloat(v) || 0); setD(false); } }}
      style={{width:'90px',padding:'.3rem .4rem',border:'1px solid var(--border)',borderRadius:6,fontSize:'.78rem',background:disabled?'#f3f4f6':'#fff',textAlign:'right',fontFamily:'inherit'}}
    />
  );
}

const fmtE  = n => Number(n||0).toLocaleString('es-ES',{minimumFractionDigits:2,maximumFractionDigits:2})+' €';
const fmt   = d => _fmtDateUTC(d);

function KPI({ label, value, sub, color }) {
  return (
    <div className="est-kpi">
      <div className="est-kpi-label">{label}</div>
      <div className="est-kpi-value" style={color?{color}:{}}>{value}</div>
      {sub && <div className="est-kpi-sub">{sub}</div>}
    </div>
  );
}

export default function FacturacionPage() {
  const { auth } = useContext(AuthContext);

  const [ops,        setOps]        = useState([]);
  const [opsLoading, setOpsLoading] = useState(true);
  const [savingOps,  setSavingOps]  = useState({});
  const [error,      setError]      = useState('');

  // Filtros
  const [fYear,       setFYear]       = useState(String(new Date().getFullYear()));
  const [fMonth,      setFMonth]      = useState('');
  const [fDay,        setFDay]        = useState('');
  const [fComercial,  setFComercial]  = useState('');
  const [fClientName, setFClientName] = useState('');
  const [fStatus,     setFStatus]     = useState(''); // '' = todos

  useEffect(() => {
    apiGet('/accounting/operations', auth.token)
      .then(setOps)
      .catch(e => { setError(e.message); setOps([]); })
      .finally(() => setOpsLoading(false));
  }, [auth.token]);

  async function saveOp(leadId, patch) {
    setSavingOps(prev => ({ ...prev, [leadId]: true }));
    try {
      await apiPatch(`/accounting/operations/${leadId}`, patch, auth.token);
      const fresh = await apiGet('/accounting/operations', auth.token);
      setOps(fresh);
    } catch (e) {
      alert('Error guardando: ' + e.message);
    } finally {
      setSavingOps(prev => { const n = { ...prev }; delete n[leadId]; return n; });
    }
  }

  if (opsLoading) return <div className="est-loading">Cargando facturación…</div>;
  if (error)      return <div className="est-error">{error}</div>;

  const dateOf = o => {
    const s = o.createdAt;
    if (!s) return null;
    return new Date(String(s).replace(' ', 'T'));
  };

  const yearsDisponibles = Array.from(new Set(ops.map(o => { const d = dateOf(o); return d ? d.getFullYear() : null; }).filter(Boolean))).sort((a,b) => b - a);
  const comercialesDisp  = Array.from(new Set(ops.map(o => o.comercialName).filter(Boolean))).sort();

  const clientNeedle = fClientName.trim().toLowerCase();

  const opsFiltradas = ops.filter(o => {
    const d = dateOf(o);
    if (!d) return false;
    if (fYear  && d.getFullYear() !== parseInt(fYear,10)) return false;
    if (fMonth && (d.getMonth()+1) !== parseInt(fMonth,10)) return false;
    if (fDay   && d.getDate() !== parseInt(fDay,10)) return false;
    if (fComercial && (o.comercialName || 'Sin asignar') !== fComercial) return false;
    if (fStatus) {
      if (fStatus === 'firmado') {
        if (o.status !== 'firmado' && o.status !== 'ganado') return false;
      } else if (o.status !== fStatus) return false;
    }
    if (clientNeedle && !(o.leadName||'').toLowerCase().includes(clientNeedle)) return false;
    return true;
  });

  function resetFiltros() {
    setFYear(String(new Date().getFullYear()));
    setFMonth(''); setFDay(''); setFComercial(''); setFClientName(''); setFStatus('');
  }
  const filtrosActivos = !!fMonth || !!fDay || !!fComercial || !!fClientName || !!fStatus || fYear !== String(new Date().getFullYear());

  const MES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

  // ── Globales (sobre operaciones filtradas) ──
  const totales = opsFiltradas.reduce((acc, o) => ({
    budget:       acc.budget       + Number(o.budgetAccepted    || 0),
    paid:         acc.paid         + Number(o.amountPaid        || 0),
    factory:      acc.factory      + Number(o.factoryCost       || 0),
    transport:    acc.transport    + Number(o.transportCost     || 0),
    install:      acc.install      + Number(o.installationCost  || 0),
    cash:         acc.cash         + Number(o.cashPaymentAmount || 0),
    ivaCollected: acc.ivaCollected + Number(o.ivaCollected      || 0),
    ivaOnCosts:   acc.ivaOnCosts   + Number(o.ivaOnCosts        || 0),
    ivaNet:       acc.ivaNet       + Number(o.ivaNet ?? o.ivaAmount ?? 0),
    net:          acc.net          + Number(o.netProfit         || 0),
  }), { budget:0, paid:0, factory:0, transport:0, install:0, cash:0, ivaCollected:0, ivaOnCosts:0, ivaNet:0, net:0 });

  const totalCostes = totales.factory + totales.transport + totales.install;
  const countReservado   = opsFiltradas.filter(o => o.status === 'reservado').length;
  const countPrimerPago  = opsFiltradas.filter(o => o.status === 'primer_pago').length;
  const countFirmado     = opsFiltradas.filter(o => o.status === 'firmado' || o.status === 'ganado').length;

  return (
    <div className="est-page">
      <h2 style={{marginBottom:'.25rem',fontWeight:700}}>Facturación</h2>
      <p style={{color:'var(--muted)',fontSize:'.85rem',marginBottom:'1.5rem'}}>
        Globales y operaciones detalladas. Los filtros se aplican a ambas secciones.
      </p>

      {/* ── Filtros ── */}
      {ops.length > 0 && (
        <div className="est-card" style={{marginBottom:'1rem',padding:'.85rem 1rem'}}>
          <div style={{display:'flex',flexWrap:'wrap',gap:'.6rem',alignItems:'flex-end'}}>
            <div style={{display:'flex',flexDirection:'column',gap:4}}>
              <label style={{fontSize:'.7rem',textTransform:'uppercase',letterSpacing:'.06em',color:'var(--muted)',fontWeight:600}}>Año</label>
              <select value={fYear} onChange={e => setFYear(e.target.value)}
                style={{padding:'.4rem .55rem',border:'1px solid var(--border)',borderRadius:6,fontSize:'.85rem',background:'#fff',fontFamily:'inherit',minWidth:90}}>
                {(yearsDisponibles.length ? yearsDisponibles : [new Date().getFullYear()]).map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:4}}>
              <label style={{fontSize:'.7rem',textTransform:'uppercase',letterSpacing:'.06em',color:'var(--muted)',fontWeight:600}}>Mes</label>
              <select value={fMonth} onChange={e => { setFMonth(e.target.value); setFDay(''); }}
                style={{padding:'.4rem .55rem',border:'1px solid var(--border)',borderRadius:6,fontSize:'.85rem',background:'#fff',fontFamily:'inherit',minWidth:130}}>
                <option value="">Todos los meses</option>
                {MES.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
              </select>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:4}}>
              <label style={{fontSize:'.7rem',textTransform:'uppercase',letterSpacing:'.06em',color:'var(--muted)',fontWeight:600}}>Día</label>
              <select value={fDay} onChange={e => setFDay(e.target.value)} disabled={!fMonth}
                style={{padding:'.4rem .55rem',border:'1px solid var(--border)',borderRadius:6,fontSize:'.85rem',background:fMonth?'#fff':'#f3f4f6',fontFamily:'inherit',minWidth:90}}>
                <option value="">Todos</option>
                {Array.from({length:31}, (_,i) => i+1).map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:4,minWidth:170}}>
              <label style={{fontSize:'.7rem',textTransform:'uppercase',letterSpacing:'.06em',color:'var(--muted)',fontWeight:600}}>Comercial</label>
              <select value={fComercial} onChange={e => setFComercial(e.target.value)}
                style={{padding:'.4rem .55rem',border:'1px solid var(--border)',borderRadius:6,fontSize:'.85rem',background:'#fff',fontFamily:'inherit'}}>
                <option value="">Todos los comerciales</option>
                {comercialesDisp.map(c => <option key={c} value={c}>{c}</option>)}
                {ops.some(o => !o.comercialName) && <option value="Sin asignar">Sin asignar</option>}
              </select>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:4,minWidth:170}}>
              <label style={{fontSize:'.7rem',textTransform:'uppercase',letterSpacing:'.06em',color:'var(--muted)',fontWeight:600}}>Proceso</label>
              <select value={fStatus} onChange={e => setFStatus(e.target.value)}
                style={{padding:'.4rem .55rem',border:'1px solid var(--border)',borderRadius:6,fontSize:'.85rem',background:'#fff',fontFamily:'inherit'}}>
                <option value="">Todos los procesos</option>
                <option value="reservado">En proceso · 100€</option>
                <option value="primer_pago">70% pagado</option>
                <option value="firmado">Cerrado / firmado</option>
              </select>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:4,flex:1,minWidth:200}}>
              <label style={{fontSize:'.7rem',textTransform:'uppercase',letterSpacing:'.06em',color:'var(--muted)',fontWeight:600}}>Cliente</label>
              <input type="text" value={fClientName} onChange={e => setFClientName(e.target.value)} placeholder="Buscar por nombre…"
                style={{padding:'.4rem .55rem',border:'1px solid var(--border)',borderRadius:6,fontSize:'.85rem',background:'#fff',fontFamily:'inherit'}}/>
            </div>
            {filtrosActivos && (
              <button onClick={resetFiltros}
                style={{padding:'.4rem .8rem',background:'transparent',color:'var(--muted)',border:'1px solid var(--border)',borderRadius:6,cursor:'pointer',fontSize:'.78rem',fontFamily:'inherit'}}>
                ✕ Limpiar
              </button>
            )}
            <div style={{fontSize:'.75rem',color:'var(--muted)',marginLeft:'auto',alignSelf:'center'}}>
              {opsFiltradas.length} de {ops.length} operaciones
            </div>
          </div>
        </div>
      )}

      {/* ── Globales ── */}
      <div className="est-section-title">Globales</div>
      <div className="est-kpi-grid" style={{marginBottom:'.6rem'}}>
        <KPI label="Operaciones"          value={String(opsFiltradas.length)} sub={`${countReservado} en 100€ · ${countPrimerPago} en 70% · ${countFirmado} cerradas`} color="var(--primary)"/>
        <KPI label="Facturado total"      value={fmtE(totales.budget)} sub="Importe total de los presupuestos"/>
        <KPI label="Cobrado"              value={fmtE(totales.paid)}   sub="Importe realmente ingresado"/>
        <KPI label="Beneficio neto"       value={fmtE(totales.net)}    sub="Tras costes y compensación IVA" color={totales.net >= 0 ? '#16a34a' : '#dc2626'}/>
      </div>
      <div className="est-kpi-grid" style={{marginBottom:'1.25rem'}}>
        <KPI label="Coste fábrica"        value={fmtE(totales.factory)}   sub="Con IVA — factura proveedor"/>
        <KPI label="Coste transporte"     value={fmtE(totales.transport)} sub="Con IVA — factura proveedor"/>
        <KPI label="Coste instalación"    value={fmtE(totales.install)}   sub="Sin IVA — instalador autónomo"/>
        <KPI label="Total costes"         value={fmtE(totalCostes)}       sub="Suma de los tres conceptos" color="#dc2626"/>
        <KPI label="IVA neto"             value={fmtE(totales.ivaNet)}    sub={`Rep. ${fmtE(totales.ivaCollected)} − Sop. ${fmtE(totales.ivaOnCosts)}`}/>
      </div>

      {/* ── Operaciones detalladas ── */}
      <div className="est-section-title">Operaciones detalladas</div>
      <p style={{fontSize:'.78rem',color:'var(--muted)',marginTop:-8,marginBottom:'.7rem'}}>
        Edita los costes (fábrica, transporte, instalación — todos los importes <strong>con IVA</strong>, tal como aparecen en la factura) y el efectivo cobrado. El <strong>IVA soportado</strong> en costes se compensa con el repercutido. A partir del primer pago el beneficio se proyecta sobre el <strong>precio real de venta</strong>, no sobre lo cobrado hasta la fecha.
      </p>
      {opsFiltradas.length === 0 ? (
        <div className="est-card"><div className="est-empty">{ops.length === 0 ? 'No hay operaciones registradas todavía.' : 'No hay operaciones que coincidan con los filtros.'}</div></div>
      ) : (
        <div className="est-card est-card--no-pad" style={{marginBottom:'1rem'}}>
          <div className="est-table-wrap" style={{overflowX:'auto'}}>
            <table className="est-table" style={{minWidth:1200}}>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Comercial</th>
                  <th>Estado</th>
                  <th>Fecha</th>
                  <th style={{textAlign:'right'}}>Total ppto</th>
                  <th style={{textAlign:'right'}}>Pagado</th>
                  <th style={{textAlign:'right'}}>Coste fábrica</th>
                  <th style={{textAlign:'right'}}>Transporte</th>
                  <th style={{textAlign:'right'}}>Instalación</th>
                  <th style={{textAlign:'right'}}>Efectivo</th>
                  <th style={{textAlign:'right'}}>IVA</th>
                  <th style={{textAlign:'right'}}>Beneficio</th>
                </tr>
              </thead>
              <tbody>
                {opsFiltradas.map(o => {
                  const isClosed = o.status === 'firmado' || o.status === 'ganado';
                  const enabledEdit = o.status === 'primer_pago' || isClosed;
                  return (
                    <tr key={o.id} style={savingOps[o.id] ? {opacity:.6} : {}}>
                      <td className="est-td-name">
                        {o.leadName}
                        <div style={{fontSize:'.7rem',color:'var(--muted)'}}>{o.leadCode}</div>
                      </td>
                      <td>{o.comercialName || <span className="est-muted">Sin asignar</span>}</td>
                      <td><StatusBadge status={o.status}/></td>
                      <td className="est-muted" style={{whiteSpace:'nowrap'}}>{fmt(o.createdAt)}</td>
                      <td style={{textAlign:'right',fontWeight:600}}>{fmtE(o.budgetAccepted)}</td>
                      <td style={{textAlign:'right'}}>{fmtE(o.amountPaid)}</td>
                      <td style={{textAlign:'right'}}>
                        <EditableCell value={o.factoryCost} disabled={!enabledEdit || !!savingOps[o.id]}
                          onSave={v => saveOp(o.id, { factory_cost: v })}/>
                      </td>
                      <td style={{textAlign:'right'}}>
                        <EditableCell value={o.transportCost} disabled={!enabledEdit || !!savingOps[o.id]}
                          onSave={v => saveOp(o.id, { transport_cost: v })}/>
                      </td>
                      <td style={{textAlign:'right'}}>
                        <EditableCell value={o.installationCost} disabled={!enabledEdit || !!savingOps[o.id]}
                          onSave={v => saveOp(o.id, { installation_cost: v })}/>
                      </td>
                      <td style={{textAlign:'right'}}>
                        <EditableCell value={o.cashPaymentAmount} disabled={!enabledEdit || !!savingOps[o.id]}
                          onSave={v => saveOp(o.id, { cash_payment_amount: v })}/>
                      </td>
                      <td style={{textAlign:'right',fontSize:'.72rem',lineHeight:1.4,whiteSpace:'nowrap'}}>
                        <div style={{color:'#16a34a'}} title="IVA repercutido (cobrado al cliente)">Rep. +{fmtE(o.ivaCollected ?? 0)}</div>
                        <div style={{color:'#dc2626'}} title="IVA soportado (pagado a proveedores)">Sop. −{fmtE(o.ivaOnCosts ?? 0)}</div>
                        <div style={{borderTop:'1px solid var(--border)',paddingTop:2,marginTop:2,fontWeight:700,color:(o.ivaNet ?? 0)>=0?'#374151':'#16a34a'}} title="IVA neto: lo que se debe a Hacienda (+) o se recupera (−)">
                          {(o.ivaNet ?? 0) >= 0 ? '' : '+'}{fmtE(o.ivaNet ?? 0)}
                        </div>
                      </td>
                      <td style={{textAlign:'right',fontWeight:700,color:o.netProfit>=0?'#16a34a':'#dc2626'}}>{fmtE(o.netProfit)}</td>
                    </tr>
                  );
                })}
                <tr style={{background:'var(--bg)',borderTop:'2px solid var(--border)',fontWeight:700}}>
                  <td colSpan={4} style={{textAlign:'right',color:'var(--muted)'}}>TOTALES ({opsFiltradas.length}):</td>
                  <td style={{textAlign:'right'}}>{fmtE(totales.budget)}</td>
                  <td style={{textAlign:'right'}}>{fmtE(totales.paid)}</td>
                  <td style={{textAlign:'right',color:'#dc2626'}}>−{fmtE(totales.factory)}</td>
                  <td style={{textAlign:'right',color:'#dc2626'}}>−{fmtE(totales.transport)}</td>
                  <td style={{textAlign:'right',color:'#dc2626'}}>−{fmtE(totales.install)}</td>
                  <td style={{textAlign:'right'}}>{fmtE(totales.cash)}</td>
                  <td style={{textAlign:'right',fontSize:'.72rem',lineHeight:1.4,whiteSpace:'nowrap'}}>
                    <div style={{color:'#16a34a'}}>Rep. +{fmtE(totales.ivaCollected)}</div>
                    <div style={{color:'#dc2626'}}>Sop. −{fmtE(totales.ivaOnCosts)}</div>
                    <div style={{borderTop:'1px solid var(--border)',paddingTop:2,marginTop:2,color:totales.ivaNet>=0?'#374151':'#16a34a'}}>
                      {totales.ivaNet >= 0 ? '' : '+'}{fmtE(totales.ivaNet)}
                    </div>
                  </td>
                  <td style={{textAlign:'right',color:totales.net>=0?'#16a34a':'#dc2626'}}>{fmtE(totales.net)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
